/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

// Needed for redux-saga es6 generator support
import '@babel/polyfill';

// Import all the third party stuff
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import TagManager from 'react-gtm-module';
import { HistoryRouter as Router } from 'redux-first-history/rr6';

import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import zipy from 'zipyai';

import Analytics from 'utils/analytics';
import 'sanitize.css/sanitize.css';
import './styles/app.scss';
import {
  GlobalStyle as CashfreeGlobalStyle,
  I18nSetup,
} from '@cashfree-intl/coherent';
// Import script loader component

// Import theme
import Theme from 'components/Theme';

// Import root app
import App from 'containers/App';
import Toast from 'containers/Toast';
import ErrorBoundary from 'containers/ErrorBoundary';
// Import Language Provider
import { I18nextProvider } from 'react-i18next';
import { sentryBeforeSend } from 'utils/errorLogger';
import errorMessages from '../externaliseError/validationErrors.json';

// Load the favicon and the .htaccess file
/* eslint-disable import/no-unresolved, import/extensions */
// import '!file-loader?name=[name].[ext]!./images/favicon.ico';
// import 'file-loader?name=.htaccess!./.htaccess';
/* eslint-enable import/no-unresolved, import/extensions */

import { store, history } from './store';
import i18n from './i18n';
import GlobalStyle from './global-styles';

const MOUNT_NODE = document.getElementById('app');
const root = createRoot(MOUNT_NODE);

const envObj = process.env;

// enable sentry on production
// enabling this on prod-test for testing, will revert this post testing
if (['prod-test', 'prod'].includes(envObj.NODE_ENV) && envObj.SENTRY_DSN) {
  Sentry.init({
    dsn: envObj.SENTRY_DSN,
    integrations: [new Integrations.BrowserTracing()],
    tracesSampleRate: 0.1,
    beforeSend: sentryBeforeSend,
  });
  zipy.init('4767aef5');
}

const renderApp = () => {
  root.render(
    <Router history={history}>
      <I18nSetup messages={{ en: errorMessages }}>
        <Provider store={store}>
          <I18nextProvider i18n={i18n}>
            <Theme>
              <React.Fragment>
                <CashfreeGlobalStyle />
                <GlobalStyle />
                <ErrorBoundary>
                  <>
                    <Toast />
                    <App />
                  </>
                </ErrorBoundary>
              </React.Fragment>
            </Theme>
          </I18nextProvider>
        </Provider>
      </I18nSetup>
    </Router>,
    MOUNT_NODE,
  );
};

if (module.hot) {
  // Hot reloadable React components and translation json files
  // modules.hot.accept does not accept dynamic dependencies,
  // have to be constants at compile-time
  module.hot.accept(['containers/App'], () => {
    // ReactDOM.unmountComponentAtNode(MOUNT_NODE);
    root.unmount();
    renderApp();
  });
}

renderApp();

Analytics.init();

// Install ServiceWorker and AppCache in the end since
// it's not most important operation and if main code fails,
// we do not want it installed
if (process.env.NODE_ENV === 'production') {
  // require('offline-plugin/runtime').install(); // eslint-disable-line global-require

  const tagManagerArgs = {
    gtmId: 'GTM-5SRVH4P',
  };

  TagManager.initialize(tagManagerArgs);
}
